/**
    * @license
    * Copyright 2019 Google LLC. All Rights Reserved.
    * Licensed under the Apache License, Version 2.0 (the "License");
    * you may not use this file except in compliance with the License.
    * You may obtain a copy of the License at
    *
    * http://www.apache.org/licenses/LICENSE-2.0
    *
    * Unless required by applicable law or agreed to in writing, software
    * distributed under the License is distributed on an "AS IS" BASIS,
    * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
    * See the License for the specific language governing permissions and
    * limitations under the License.
    * =============================================================================
    */
import { util, tidy, keep, div } from "@tensorflow/tfjs-core";

function __awaiter(t, a, e, s) {
  return new (e || (e = Promise))(function (r, i) {
    function n(t) {
      try {
        o(s.next(t));
      } catch (t) {
        i(t);
      }
    }

    function l(t) {
      try {
        o(s.throw(t));
      } catch (t) {
        i(t);
      }
    }

    function o(t) {
      t.done ? r(t.value) : new e(function (a) {
        a(t.value);
      }).then(n, l);
    }

    o((s = s.apply(t, a || [])).next());
  });
}

function __generator(t, a) {
  var e,
      s,
      r,
      i,
      n = {
    label: 0,
    sent: function sent() {
      if (1 & r[0]) throw r[1];
      return r[1];
    },
    trys: [],
    ops: []
  };
  return i = {
    next: l(0),
    throw: l(1),
    return: l(2)
  }, "function" == typeof Symbol && (i[Symbol.iterator] = function () {
    return this;
  }), i;

  function l(i) {
    return function (l) {
      return function (i) {
        if (e) throw new TypeError("Generator is already executing.");

        for (; n;) {
          try {
            if (e = 1, s && (r = 2 & i[0] ? s.return : i[0] ? s.throw || ((r = s.return) && r.call(s), 0) : s.next) && !(r = r.call(s, i[1])).done) return r;

            switch (s = 0, r && (i = [2 & i[0], r.value]), i[0]) {
              case 0:
              case 1:
                r = i;
                break;

              case 4:
                return n.label++, {
                  value: i[1],
                  done: !1
                };

              case 5:
                n.label++, s = i[1], i = [0];
                continue;

              case 7:
                i = n.ops.pop(), n.trys.pop();
                continue;

              default:
                if (!(r = (r = n.trys).length > 0 && r[r.length - 1]) && (6 === i[0] || 2 === i[0])) {
                  n = 0;
                  continue;
                }

                if (3 === i[0] && (!r || i[1] > r[0] && i[1] < r[3])) {
                  n.label = i[1];
                  break;
                }

                if (6 === i[0] && n.label < r[1]) {
                  n.label = r[1], r = i;
                  break;
                }

                if (r && n.label < r[2]) {
                  n.label = r[2], n.ops.push(i);
                  break;
                }

                r[2] && n.ops.pop(), n.trys.pop();
                continue;
            }

            i = a.call(t, n);
          } catch (t) {
            i = [6, t], s = 0;
          } finally {
            e = r = 0;
          }
        }

        if (5 & i[0]) throw i[1];
        return {
          value: i[0] ? i[1] : void 0,
          done: !0
        };
      }([i, l]);
    };
  }
}

function concatWithNulls(t, a) {
  return null == t && null == a ? null : null == t ? a.clone() : null === a ? t.clone() : t.concat(a, 0);
}

function topK(t, a) {
  for (var e = [], s = 0; s < t.length; s++) {
    e.push({
      value: t[s],
      index: s
    });
  }

  e.sort(function (t, a) {
    return a.value - t.value;
  });
  var r = new Float32Array(a),
      i = new Int32Array(a);

  for (s = 0; s < a; s++) {
    r[s] = e[s].value, i[s] = e[s].index;
  }

  return {
    values: r,
    indices: i
  };
}

var version = "1.2.2",
    KNNClassifier = function () {
  function t() {
    this.classDatasetMatrices = {}, this.classExampleCount = {}, this.labelToClassId = {}, this.nextClassId = 0;
  }

  return t.prototype.addExample = function (t, a) {
    var e = this;
    if (null == this.exampleShape && (this.exampleShape = t.shape), !util.arraysEqual(this.exampleShape, t.shape)) throw new Error("Example shape provided, " + t.shape + " does not match previously provided example shapes " + this.exampleShape + ".");
    this.clearTrainDatasetMatrix(), a in this.labelToClassId || (this.labelToClassId[a] = this.nextClassId++), tidy(function () {
      var s = e.normalizeVectorToUnitLength(t.flatten()),
          r = s.shape[0];
      if (null == e.classDatasetMatrices[a]) e.classDatasetMatrices[a] = s.as2D(1, r);else {
        var i = e.classDatasetMatrices[a].as2D(e.classExampleCount[a], r).concat(s.as2D(1, r), 0);
        e.classDatasetMatrices[a].dispose(), e.classDatasetMatrices[a] = i;
      }
      keep(e.classDatasetMatrices[a]), null == e.classExampleCount[a] && (e.classExampleCount[a] = 0), e.classExampleCount[a]++;
    });
  }, t.prototype.similarities = function (t) {
    var a = this;
    return tidy(function () {
      var e = a.normalizeVectorToUnitLength(t.flatten()),
          s = e.shape[0];

      if (null == a.trainDatasetMatrix) {
        var r = null;

        for (var i in a.classDatasetMatrices) {
          r = concatWithNulls(r, a.classDatasetMatrices[i]);
        }

        a.trainDatasetMatrix = r;
      }

      if (null == a.trainDatasetMatrix) return console.warn("Cannot predict without providing training examples."), null;
      keep(a.trainDatasetMatrix);
      var n = a.getNumExamples();
      return a.trainDatasetMatrix.as2D(n, s).matMul(e.as2D(s, 1)).as1D();
    });
  }, t.prototype.predictClass = function (t, a) {
    return void 0 === a && (a = 3), __awaiter(this, void 0, void 0, function () {
      var e,
          s,
          r,
          i,
          n = this;
      return __generator(this, function (l) {
        switch (l.label) {
          case 0:
            if (a < 1) throw new Error("Please provide a positive integer k value to predictClass.");
            if (0 === this.getNumExamples()) throw new Error("You have not added any examples to the KNN classifier. Please add examples before calling predictClass.");
            return e = tidy(function () {
              return n.similarities(t).asType("float32");
            }), s = Math.min(a, this.getNumExamples()), i = topK, [4, e.data()];

          case 1:
            return r = i.apply(void 0, [l.sent(), s]).indices, e.dispose(), [2, this.calculateTopClass(r, s)];
        }
      });
    });
  }, t.prototype.clearClass = function (t) {
    if (null == this.classDatasetMatrices[t]) throw new Error("Cannot clear invalid class " + t);
    this.classDatasetMatrices[t].dispose(), delete this.classDatasetMatrices[t], delete this.classExampleCount[t], this.clearTrainDatasetMatrix();
  }, t.prototype.clearAllClasses = function () {
    for (var t in this.classDatasetMatrices) {
      this.clearClass(t);
    }
  }, t.prototype.getClassExampleCount = function () {
    return this.classExampleCount;
  }, t.prototype.getClassifierDataset = function () {
    return this.classDatasetMatrices;
  }, t.prototype.getNumClasses = function () {
    return Object.keys(this.classExampleCount).length;
  }, t.prototype.setClassifierDataset = function (t) {
    for (var a in this.clearTrainDatasetMatrix(), this.classDatasetMatrices = t, t) {
      this.classExampleCount[a] = t[a].shape[0];
    }
  }, t.prototype.calculateTopClass = function (t, a) {
    var e,
        s = {};
    if (null == t) return {
      classIndex: this.labelToClassId[e],
      label: e,
      confidences: s
    };
    var r = {},
        i = 0;

    for (var n in this.classDatasetMatrices) {
      i += this.classExampleCount[n], r[n] = i;
    }

    var l = {};

    for (var n in this.classDatasetMatrices) {
      l[n] = 0;
    }

    for (var o = 0; o < t.length; o++) {
      var c = t[o];

      for (var n in this.classDatasetMatrices) {
        if (c < r[n]) {
          l[n]++;
          break;
        }
      }
    }

    var u = 0;

    for (var n in this.classDatasetMatrices) {
      var p = l[n] / a;
      p > u && (u = p, e = n), s[n] = p;
    }

    return {
      classIndex: this.labelToClassId[e],
      label: e,
      confidences: s
    };
  }, t.prototype.clearTrainDatasetMatrix = function () {
    null != this.trainDatasetMatrix && (this.trainDatasetMatrix.dispose(), this.trainDatasetMatrix = null);
  }, t.prototype.normalizeVectorToUnitLength = function (t) {
    return tidy(function () {
      var a = t.norm();
      return div(t, a);
    });
  }, t.prototype.getNumExamples = function () {
    var t = 0;

    for (var a in this.classDatasetMatrices) {
      t += this.classExampleCount[a];
    }

    return t;
  }, t.prototype.dispose = function () {
    for (var t in this.clearTrainDatasetMatrix(), this.classDatasetMatrices) {
      this.classDatasetMatrices[t].dispose();
    }
  }, t;
}();

function create() {
  return new KNNClassifier();
}

export { KNNClassifier, create, version };